<template>
  <MdtMailPreview
    v-if="mailPreviewLoaded"
    :title="'general_write_email' | translate"
    :mail-preview="mailData.mailPreview"
    :mail-template="mailData.mailTemplate"
    :customization-not-allowed="mailData.customizationNotAllowed"
    :mail-signature="mailData.mailSignature"
    :templates="mailData.templates"
    :contacts="mailData.contactGroups"
    :send-mail-disabled="sendMailDisabled"
    :allow-attachment-upload="allowAttachmentUpload"
    :selected-rows="selectedRows"
    :show-pdf-download-btn="showPdfDownloadButton"
    :show-info-card-close-btn="showInfoCardCloseBtn"
    @getMailPreview="getMailPreview"
    @sendMails="sendMails"
    @close="$emit('close')">
    <div
      slot="section-right-label"
      class="section-right-label">
      {{ formattedTotalNumberOfReceivers }}
    </div>
    <div
      v-if="additionalReceivers"
      slot="additional-receivers"
      class="additional-receivers">
      {{ additionalReceiversLabel }}
    </div>
  </MdtMailPreview>
</template>

<script>
import { pdf } from '@/utility';
import { BACKGROUND_TASK } from '@/utility/constants';

const { AVAILABLE_VIEWS } = BACKGROUND_TASK;

export default {
  name: 'ModalBulkMail',
  props: {
    selectedRows: {
      type: Array,
      required: true,
    },
    bulkOption: {
      type: Object,
      required: true,
    },
    tableId: {
      type: String,
      default: '',
    },
  },
  data() {
    const { translate } = this.$options.filters;
    return {
      sendMailDisabled: false,
      mailPreviewLoaded: false,
      mailData: {
        templates: [],
        contacts: [],
        customizationNotAllowed: false,
        mailTemplate: {
          subject: '',
          body: '',
        },
        mailPreview: {
          subject: '',
          body: '',
        },
        mailSignature: {
          subject: '',
          body: '',
        },
      },
      loading: false,
      translate,
    };
  },
  computed: {
    allowAttachmentUpload() {
      return this.$route.name === 'Applications' || this.$route.name === 'Waitlist'
        || (this.$route.name === 'ApartmentDetails' && ['related_applications', 'related_interested_applications'].includes(this.bulkOption.mailView))
        || this.$route.name === 'Tenant' || this.$route.name === 'InterestedPeople'
        || this.$route.name === 'InterestedPeopleB2B' || this.$route.name === 'Investors'
        || (this.$route.name === 'ProjectDetails' && ['investors_with_offer', 'matching_waitlist'].includes(this.bulkOption.mailView));
    },
    showPdfDownloadButton() {
      const { name } = this.$route;
      const { mailView } = this.bulkOption;
      const mailViewBulkOptions = ['related_applications', 'related_interested_applications'];
      const excludedRoutes = ['InterestedPeople', 'Applications', 'Tenant', 'ApartmentDetails', 'Waitlist'];
      return !excludedRoutes.includes(name)
        && (name !== 'ApartmentDetails' || !mailViewBulkOptions.includes(mailView));
    },
    table() {
      if (this.tableId) {
        return this.$store.getters['table/getAdditionalTable'](this.tableId);
      }
      return this.$store.state.table;
    },
    pagination() {
      return this.$store.getters['table/pagination'];
    },
    receiversCount() {
      /*
      * Background processes active:
      *   1. 4 rows selected (with 6 adults/emails) return 4
      *   2. 500 rows selected (with 1000 adults/mails) return rowsPerPage+rest
      * Background processes inactive:
      *   1. 4 rows selected (with 6 adults/emails) return 6
      *   2. 40 rows selected (with 100 adults/mails) return 100
      * */
      const { contactGroups } = this.mailData;
      const { name } = this.$route;
      const isViewActive = this.isBackgroundProcessesActive && AVAILABLE_VIEWS.includes(name);
      if (isViewActive) {
        return this.allEntriesSelected ? this.pagination.totalItems : this.selectedRows.length;
      }
      return contactGroups?.reduce((sum, group) => sum + group.contacts.length, 0) || 0;
    },
    formattedTotalNumberOfReceivers() {
      // display the title on the right side of a pop-up section
      const { totalItems } = this.pagination;
      return !this.additionalReceivers
        ? `${this.receiversCount} ${this.translate('admin_recipients')}`
        : `${totalItems} ${this.translate('admin_recipients')} ${this.translate('general_total')}`;
    },
    isBackgroundProcessesActive() {
      const { settings } = this.$store.getters.bootstrap;
      return settings?.backgroundProcesses?.bulkMail;
    },
    showInfoCardCloseBtn() {
      const { name } = this.$route;
      return !this.isBackgroundProcessesActive || !AVAILABLE_VIEWS.includes(name);
    },
    additionalReceivers() {
      const { totalItems } = this.pagination;
      if (this.receiversCount <= this.table.tableData.body.length) return false;
      return totalItems - this.table.tableData.body.length;
    },
    additionalReceiversLabel() {
      return this.translate('general_more_receivers').replace('[d]', this.additionalReceivers);
    },
    allEntriesSelected() {
      return this.$store.getters['table/getAllEntriesSelected'];
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      const targets = this.selectedRows;
      const { mailView } = this.bulkOption;
      const { actionNameGET, view } = this.getBulkMailActionName();
      this.$store.dispatch(actionNameGET, { targets, mailView, view }).then((data) => {
        this.mailData.templates = data.mailTemplates;
        this.mailData.contactGroups = data.contactGroups;
        this.mailData.mailSignature = data.newTemplate;
        this.mailData.customizationNotAllowed = data.customizationNotAllowed;
        this.mailPreviewLoaded = true;
      }).catch(this.showResponseError);
    },
    getMailPreview(data) {
      if (this.loading) return;
      const { mailView } = this.bulkOption;
      const contactGroup = this.getApplicationIdContactGroup(data.mailTemplate.adultId);
      const params = {
        ...data.mailTemplate,
        mailView,
        apartmentId: Number(this.$route.params.id),
        applicationId: contactGroup.applicationId,
      };

      /*
      * - If customizationNotAllowed exists and it's true - disable template editing
      * - Don't send body and subject so backend can provide
      * specific language preview for each template
      *  */
      if (this.mailData.customizationNotAllowed) {
        delete params.body;
        delete params.subject;
      }

      this.loading = true;
      this.$store.dispatch('table/getReferenceMailPreview', params).then((mailPreview) => {
        this.mailData.mailPreview = mailPreview;
      }).catch(this.showResponseError)
        .finally(() => {
          this.loading = false;
          data.cancelLoading();
        });
    },
    getBulkMailActionName() {
      const routeToView = {
        InterestedPeople: 'interested',
        Applications: 'application',
      };
      const view = routeToView[this.$route.name];

      if (!this.isBackgroundProcessesActive || !view) {
        return {
          actionNamePOST: 'table/sendBulkMail',
          actionNameGET: 'table/getBulkMail',
        };
      }

      return {
        actionNamePOST: 'table/sendBulkMailToAllEntries',
        actionNameGET: 'table/getBulkMailForAllEntries',
        view,
      };
    },
    sendMails(mailSettings) {
      if (this.loading) return;

      const { actionNamePOST, view } = this.getBulkMailActionName();
      const { mailView } = this.bulkOption;
      const payload = {
        ...mailSettings.params,
        mailView,
        apartmentId: Number(this.$route.params.id),
        applicationIds: this.selectedRows,
        view,
      };

      if (this.isBackgroundProcessesActive
        && AVAILABLE_VIEWS.includes(this.$route.name)) {
        // For new routes we don't need to provide targets in payload
        delete payload.targets;
      }

      if (this.allEntriesSelected) {
        const activeProgressState = this.table
          .stateFilters?.choices?.find((filter) => filter.active);
        delete payload.applicationIds;
        if (activeProgressState) payload.progressState = activeProgressState.value;
      }

      this.loading = true;
      this.sendMailDisabled = true;
      this.$store.dispatch(actionNamePOST, payload).then((data) => {
        // Download pdf if response contains pdf
        pdf.download(data.fileUrl);

        if (actionNamePOST !== 'table/sendBulkMailToAllEntries'
           || !this.isBackgroundProcessesActive) {
          this.$store.dispatch('notify', data.notify);
        }

        if (this.isBackgroundProcessesActive && AVAILABLE_VIEWS.includes(this.$route.name)) {
          this.$store.dispatch('table/getTableDataOnly');
        }
        // Refresh whole ProjectDetails page on send mail
        if (this.$route.name === 'ProjectDetails') {
          this.$store.dispatch('projects/getAllData').catch(this.showResponseError);
        }

        if (this.isBackgroundProcessesActive) {
          this.$store.dispatch('table/setAllEntriesSelected', false);
          this.$store.dispatch('table/setSelectAllOnPage', false);
        }
        this.$emit('close');
      }).catch((error) => {
        this.showResponseError(error);
        // if sending mail bounced then
        // refresh tableData to display exclamation mark on messenger icon
        // this is use cases when data need to be refreshed without loader
        const tableRoutes = ['Applications', 'Waitlist', 'Tenant', 'InterestedPeople'];
        if (tableRoutes.includes(this.$route.name)) {
          this.$store.dispatch('table/getTableDataWithoutLoader');
        }
      })
        .finally(() => {
          this.sendMailDisabled = false;
          this.loading = false;
        });
    },
    getApplicationIdContactGroup(adultId) {
      return this.mailData.contactGroups
        .find((contactGroup) => contactGroup.contacts
          .some((contact) => contact.id === adultId));
    },
  },
};
</script>
